/**
 * EditUserDialog — Phase 7 extract from pages/Users.jsx.
 *
 * Self-contained — owns form state + submit. Re-fetches users via
 * `onSaved(updatedUser)` from the parent.
 */
import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { ShieldCheck, Building2 } from "lucide-react";
import { toast } from "sonner";
import { ROLE_OPTIONS_FOR } from "@/components/users/constants";
import { fetchAssignableRoles } from "@/lib/roles-registry";
import { roleLabel } from "@/lib/terminology";
import {
    SsoProviderIcon, ssoProviderLabel, fetchTenantSsoProviders,
} from "@/lib/sso-providers";

export default function EditUserDialog({
    target, onOpenChange, me, orgs, tenantsByOrg, loadTenantsForOrg, onSaved,
}) {
    const [form, setForm] = useState({ name: "", role: "viewer" });
    const [busy, setBusy] = useState(false);
    const [enabledProviders, setEnabledProviders] = useState([]);
    const [ssoWarningAck, setSsoWarningAck] = useState(false);
    const [assignable, setAssignable] = useState(null);

    useEffect(() => {
        const orgId = form.org_id || target?.org_id;
        if (!orgId && me?.role !== "super_admin") return;
        fetchAssignableRoles(orgId || null)
            .then((rows) => {
                let visible = rows;
                if (me?.role !== "super_admin") visible = rows.filter((r) => r.code !== "super_admin");
                // ALWAYS include the user's current role even if it's
                // been disabled — we never silently strip existing
                // assignments; the admin must explicitly change it.
                if (target?.role && !visible.find((r) => r.code === target.role)) {
                    visible = [...visible, { code: target.role, name: roleLabel(target.role), _legacy: true }];
                }
                setAssignable(visible);
            })
            .catch(() => setAssignable(null));
    }, [form.org_id, target?.org_id, target?.role, me?.role]);

    useEffect(() => {
        if (target) {
            setForm({
                name: target.name || "",
                role: target.role,
                org_id: target.org_id || "",
                tenant_id: target.tenant_id || "",
                sso_required: !!target.sso_required,
                // iter-120 round-4 follow-up #12 — preserve per-user
                // SSO provider pin so admins can re-target a user.
                sso_provider: target.sso_provider || "",
            });
            setSsoWarningAck(false);
            if (me?.role === "super_admin" && target.org_id) loadTenantsForOrg(target.org_id);
            fetchTenantSsoProviders().then(setEnabledProviders);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [target]);

    const submit = async () => {
        if (!target) return;
        if (!form.name.trim()) { toast.error("Name cannot be empty"); return; }
        if (form.sso_required) {
            if (!target.sso_required && !ssoWarningAck) {
                toast.error("Please confirm that the SSO email matches.");
                return;
            }
            if (enabledProviders.length === 0) {
                toast.error("No SSO provider configured for this entity. Set one up in Settings → SSO first.");
                return;
            }
            if (!form.sso_provider) {
                if (enabledProviders.length === 1) {
                    form.sso_provider = enabledProviders[0]; // auto-pin
                } else {
                    toast.error("Pick which SSO provider this user should sign in with.");
                    return;
                }
            }
        }
        setBusy(true);
        try {
            const payload = {
                name: form.name.trim(),
                role: form.role,
                sso_required: !!form.sso_required,
                sso_provider: form.sso_required ? (form.sso_provider || null) : null,
            };
            if (me?.role === "super_admin") {
                payload.org_id = form.org_id;
                payload.tenant_id = form.tenant_id;
            }
            const r = await api.patch(`/users/${target.id}`, payload);
            toast.success("User updated");
            onSaved(r.data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Update failed");
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={!!target} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-lg">
                <DialogHeader><DialogTitle>Edit user</DialogTitle></DialogHeader>
                {target && (
                    <>
                        <div className="space-y-3">
                            <div>
                                <label className="eyebrow mb-1.5 block">Email</label>
                                <div className="h-10 px-3 flex items-center bg-secondary/40 border border-border rounded-sm text-xs font-mono text-muted-foreground" data-testid="edit-email-display">
                                    {target.email}
                                </div>
                            </div>
                            <div>
                                <label className="eyebrow mb-1.5 block">Full name</label>
                                <input value={form.name}
                                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                                    className="w-full h-10 px-3 bg-transparent border border-border rounded-sm text-sm"
                                    data-testid="edit-name-input" />
                            </div>
                            <div>
                                <label className="eyebrow mb-1.5 block">Role</label>
                                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                                    <SelectTrigger className="h-10 rounded-sm" data-testid="edit-role-select"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        {(assignable !== null
                                            ? assignable.map((r) => ({ value: r.code, label: r.name || roleLabel(r.code) }))
                                            : ROLE_OPTIONS_FOR(me?.role)
                                        ).map((o) => (
                                            <SelectItem key={o.value} value={o.value} data-testid={`edit-role-option-${o.value}`}>{o.label}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            {me?.role === "super_admin" && (
                                <>
                                    <div>
                                        <label className="eyebrow mb-1.5 block">Organization</label>
                                        <Select value={form.org_id}
                                            onValueChange={(v) => { setForm({ ...form, org_id: v, tenant_id: "" }); loadTenantsForOrg(v); }}>
                                            <SelectTrigger className="h-10 rounded-sm" data-testid="edit-org-select">
                                                <SelectValue placeholder="Select organization" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {orgs.map((o) => (
                                                    <SelectItem key={o.id} value={o.id}>
                                                        <span className="inline-flex items-center gap-2"><Building2 className="h-3.5 w-3.5 text-muted-foreground" />{o.name}</span>
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div>
                                        <label className="eyebrow mb-1.5 block">Entity</label>
                                        <Select value={form.tenant_id}
                                            onValueChange={(v) => setForm({ ...form, tenant_id: v })}
                                            disabled={!form.org_id}>
                                            <SelectTrigger className="h-10 rounded-sm" data-testid="edit-tenant-select">
                                                <SelectValue placeholder={form.org_id ? "Select entity" : "Select an organization first"} />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {(tenantsByOrg[form.org_id] || []).map((t) => (
                                                    <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                        <p className="text-[11px] text-muted-foreground font-mono mt-1.5">Changing org/entity moves the user immediately.</p>
                                    </div>
                                </>
                            )}
                            {me?.role === "org_admin" && (
                                <div>
                                    <label className="eyebrow mb-1.5 block">Entity</label>
                                    <div className="h-10 px-3 flex items-center bg-secondary/40 border border-border rounded-sm text-xs font-mono text-muted-foreground" data-testid="edit-tenant-readonly">
                                        {(tenantsByOrg[me.org_id] || []).find((t) => t.id === target.tenant_id)?.name || target.tenant_id}
                                    </div>
                                    <p className="text-[11px] text-muted-foreground font-mono mt-1.5">Only Super Admin can move users across tenants.</p>
                                </div>
                            )}

                            <div className="flex items-start gap-3 p-3 border border-border rounded-sm bg-secondary/30">
                                <Switch checked={!!form.sso_required}
                                    onCheckedChange={(v) => setForm({ ...form, sso_required: v, sso_provider: v && enabledProviders.length === 1 ? enabledProviders[0] : form.sso_provider })}
                                    data-testid="edit-sso-switch" />
                                <div className="flex-1">
                                    <div className="flex items-center gap-1.5 text-sm font-medium"><ShieldCheck className="h-3.5 w-3.5 text-primary" /> Require SSO sign-in</div>
                                    <div className="text-[11px] text-muted-foreground mt-0.5">
                                        When on, this user must authenticate via the tenant's SSO provider. Password login is disabled for their account.
                                    </div>
                                    {form.sso_required && (
                                        <div className="mt-3" data-testid="edit-sso-provider-block">
                                            <label className="eyebrow mb-1.5 block">SSO provider</label>
                                            {enabledProviders.length === 0 ? (
                                                <div className="text-[11px] text-primary font-mono" data-testid="edit-sso-no-providers">
                                                    No SSO provider configured for this tenant. Set one up in <b>Settings → SSO</b> first.
                                                </div>
                                            ) : (
                                                <Select value={form.sso_provider || ""}
                                                    onValueChange={(v) => setForm({ ...form, sso_provider: v })}>
                                                    <SelectTrigger className="h-9 rounded-sm" data-testid="edit-sso-provider-select">
                                                        <SelectValue placeholder={enabledProviders.length === 1 ? ssoProviderLabel(enabledProviders[0]) : "Select an SSO provider"} />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        {enabledProviders.map((p) => (
                                                            <SelectItem key={p} value={p} data-testid={`edit-sso-provider-option-${p}`}>
                                                                <span className="inline-flex items-center gap-2">
                                                                    <SsoProviderIcon provider={p} />
                                                                    {ssoProviderLabel(p)}
                                                                </span>
                                                            </SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                            )}
                                        </div>
                                    )}
                                    {form.sso_required && !target.sso_required && (
                                        <div className="mt-3 p-3 rounded bg-destructive/10 border border-destructive/20 text-destructive text-sm" data-testid="edit-sso-warning">
                                            <p className="font-medium mb-1">⚠️ Warning: Switching to SSO</p>
                                            <p className="text-[11px] mb-2 leading-relaxed">The user's SSO email must exactly match their current email <b>{target.email}</b>. If it does not, they will be locked out. If you are unsure, do not change the mode here.</p>
                                            <label className="flex items-center gap-2 cursor-pointer mt-2" data-testid="edit-sso-ack-label">
                                                <input type="checkbox" checked={ssoWarningAck} onChange={(e) => setSsoWarningAck(e.target.checked)} className="accent-destructive h-3.5 w-3.5 rounded-sm" data-testid="edit-sso-ack-checkbox" />
                                                <span className="text-[11px] font-medium">I confirm the SSO email matches exactly.</span>
                                            </label>
                                        </div>
                                    )}
                                    {!form.sso_required && target.sso_required && (
                                        <div className="mt-3 p-3 rounded bg-blue-500/10 border border-blue-500/20 text-blue-600 text-sm" data-testid="edit-direct-info">
                                            <p className="font-medium mb-1">ℹ️ Switching to Direct Login</p>
                                            <p className="text-[11px] leading-relaxed">When you save, the user will be logged out of their current sessions and a password reset link will be sent to <b>{target.email}</b> so they can set up a local password.</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                        <DialogFooter>
                            <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm" data-testid="edit-cancel">Cancel</Button>
                            <Button onClick={submit} disabled={busy || (form.sso_required && !target.sso_required && !ssoWarningAck)} className="rounded-sm" data-testid="edit-save">
                                {busy ? "Saving…" : "Save changes"}
                            </Button>
                        </DialogFooter>
                    </>
                )}
            </DialogContent>
        </Dialog>
    );
}
